import { useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import useNameInput from "app/pages/activation/hooks/setup/useNameInput";
import { useCreateActivationObjectMutation } from "app/pages/activation/queries/data/useCreateActivationObjectMutation";
import { ACTIVATION_PROJECT_ROUTE } from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { useSubscribeToAllAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAllAlertsMutation";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { BenchmarkGranularity } from "../../../types/common";
import type { CompetitorSchema } from "../CompetitorsStep/types";
import type { SetupModalSchema } from "../types";

type UseSetupNameSchema = {
    existingNames: SetupModalSchema["existingNames"];
    selectedCompetitors: CompetitorSchema[];
    isNameStep: boolean;
    selectedCountry: number;
    primary: CompetitorSchema;
};

export const useSetupName = ({
    existingNames,
    selectedCompetitors,
    selectedCountry,
    isNameStep,
    primary,
}: UseSetupNameSchema) => {
    const track = activationTracking.setup;
    const [shouldSubscribe, setShouldSubscribe] = useState(true);

    const onSubscribeToggle = () => {
        setShouldSubscribe((prev) => !prev);
    };

    const { onNameChange, nameError, activationSetName, setActivationSetName, validateName } =
        useNameInput(existingNames);

    const { mutateAsync: createActivationAsync, isPending: isCreating } = useCreateActivationObjectMutation();

    const { mutateAsync: subscribeToAlertsAsync, isPending: isSubscribing } = useSubscribeToAllAlertsMutation({
        subscribeToBenchmark: true,
    });

    const isNameStepDisabled = (isNameStep && !activationSetName) || (isNameStep && nameError.isError);

    const goToTheProjectPage = async (activationId: string, country: number) => {
        const { DefaultSource: webSource } = swSettings.current.resources;

        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: activationId,
            country: country,
            webSource,
            timeGranularity: BenchmarkGranularity.WEEKLY,
        });
    };

    const onCreate = async () => {
        if (nameError.isError || !activationSetName.length) {
            return;
        }

        const competitorsDomains = selectedCompetitors.map((competitor) => competitor.name);

        if (shouldSubscribe) {
            track.trackOnSubscriptionSwitcher();
        } else {
            track.trackOffSubscriptionSwitcher();
        }
        try {
            const response = await createActivationAsync({
                country: selectedCountry,
                competitorsDomains,
                primaryDomain: primary.name,
                name: activationSetName,
            });

            track.trackCreate({
                main_website: response?.primaryDomain,
                competitors: response?.competitorsDomains?.join(",") ?? "",
                country: swSettings?.current?.defaultParams?.country,
                competitive_set_id: response?.id,
            });

            if (shouldSubscribe) {
                void subscribeToAlertsAsync({ activationId: response.id });
            }

            await goToTheProjectPage(response.id, response.country);
        } catch (error) {
            Sentry.errorTracker({
                message: "Error happened while creating Activation Project",
                e: error,
                level: "error",
            });
        }
    };

    return {
        nameError,
        activationSetName,
        shouldSubscribe,
        isCreating,
        isSubscribing,
        isNameStepDisabled,
        onNameChange,
        setActivationSetName,
        onSubscribeToggle,
        onCreate,
        validateName,
    };
};
